import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import gsap from 'gsap'
import parse from 'html-react-parser'
import { ReactLenis } from 'lenis/react'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Form from '@/components/base/form'
import Modal from '@/components/base/modal'
import ScrollCta from '@/components/base/scroll-cta'
import Section from '@/components/base/section'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { useLocationCascadeFields } from '@/hooks/use-location-cascade-fields'
import { convertJsontoFormData } from '@/utils/form'
import { API } from '@/constants'
import { axiosServer } from '@/base/axios'

import { cn } from '@/lib/utils'
import { WPGravityForm, WPPageData } from '@/types'

type DownloadPageProps = {
  pageProps: WPPageData & {
    collection?: any[]
    acf?: {
      form_id?: WPGravityForm
    }
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DOWNLOAD_FORM_STORAGE_KEY = 'downloadFormData'
const DEFAULT_SITE_LANGUAGE = 'en'

const DownloadPage = ({ pageProps }: DownloadPageProps) => {
  const title = pageProps?.title?.rendered || 'Download'
  const { strings } = useStore()
  const rootRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const feedbackModalRef = useRef<HTMLDivElement>(null)
  const okButtonRef = useRef<HTMLButtonElement>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackStatus, setFeedbackStatus] = useState<'success' | 'error'>(
    'success'
  )
  const [portalMounted, setPortalMounted] = useState(false)
  const [isSilentSubmitting, setIsSilentSubmitting] = useState(false)
  const [user, setUser] = useState<{
    name?: string
    surname?: string
    email?: string
  }>({})

  const getProfileFromStoredValues = useCallback(
    (storedValues: Record<string, unknown>) => {
      const email =
        typeof storedValues.input_5 === 'string' &&
        EMAIL_REGEX.test(storedValues.input_5)
          ? storedValues.input_5
          : undefined

      if (!email) return null

      return {
        name:
          typeof storedValues.input_1 === 'string' ? storedValues.input_1 : '',
        surname:
          typeof storedValues.input_3 === 'string' ? storedValues.input_3 : '',
        email
      }
    },
    []
  )

  useEffect(() => {
    setPortalMounted(true)

    const stored = localStorage.getItem(DOWNLOAD_FORM_STORAGE_KEY)
    if (!stored) return

    try {
      const storedValues = JSON.parse(stored) as Record<string, unknown>
      const profile = getProfileFromStoredValues(storedValues)
      if (profile) setUser(profile)
    } catch {
      localStorage.removeItem(DOWNLOAD_FORM_STORAGE_KEY)
    }
  }, [getProfileFromStoredValues])

  useEffect(() => {
    const style = document.createElement('style')
    style.textContent =
      '[data-radix-popper-content-wrapper] { z-index: 200 !important; }'
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  useEffect(() => {
    if (!feedbackModalRef.current) return
    gsap.to(feedbackModalRef.current, {
      opacity: feedbackOpen ? 1 : 0,
      pointerEvents: feedbackOpen ? 'auto' : 'none',
      duration: 0.3,
      ease: 'power2.out'
    })
    if (feedbackOpen && okButtonRef.current) okButtonRef.current.focus()
  }, [feedbackOpen])

  const formId = pageProps.acf?.form_id?.id || null
  const fields = useMemo(
    () => (formId ? pageProps.acf?.form_id?.fields || [] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formId]
  )

  const {
    enhancedFields,
    handleFieldChange: handleFormFieldChange,
    selectDependencies,
    resetSelection: resetLocationSelection
  } = useLocationCascadeFields(fields, strings?.labels?.country_region_province)

  useEffect(() => {
    resetLocationSelection()
  }, [resetLocationSelection, strings?.labels?.country_region_province])

  useEffect(() => {
    if (!modalOpen) resetLocationSelection()
  }, [modalOpen, resetLocationSelection])

  const description = pageProps.acf?.form_id?.description || ''
  const [fieldOverrides, setFieldOverrides] = useState<{
    [adminLabel: string]: string
  }>({})

  const buttonText =
    pageProps.acf?.form_id?.button?.text || strings?.submit || 'Submit'

  const { locale } = useRouter()

  const getFieldDefaultValue = useCallback(
    (adminLabel: string) => {
      return fields?.find((field) => field.adminLabel === adminLabel)
        ?.defaultValue
    },
    [fields]
  )

  const buildOverrideByInputId = useCallback(
    (downloadTitle: string, downloadUrl: string) => {
      return (
        fields?.reduce(
          (acc, field) => {
            const inputId = `input_${field.id}`

            if (field.adminLabel === 'sg_site_language')
              acc[inputId] = locale || DEFAULT_SITE_LANGUAGE
            else if (field.adminLabel === 'sg_download_title')
              acc[inputId] = downloadTitle
            else if (field.adminLabel === 'sg_download_url')
              acc[inputId] = downloadUrl
            else if (field.adminLabel === 'sg_cache_id')
              acc[inputId] = field.defaultValue

            return acc
          },
          {} as Record<string, string>
        ) || {}
      )
    },
    [fields, locale]
  )

  const openModalWithOverrides = useCallback(
    (downloadTitle: string, downloadUrl: string) => {
      setFieldOverrides({
        sg_site_language: locale || DEFAULT_SITE_LANGUAGE,
        sg_download_title: downloadTitle,
        sg_download_url: downloadUrl,
        sg_cache_id: getFieldDefaultValue('sg_cache_id') || ''
      })
      setModalOpen(true)
    },
    [getFieldDefaultValue, locale]
  )

  const updateDownloadFields = useCallback(
    (title: string, url: string) => {
      const stored = localStorage.getItem(DOWNLOAD_FORM_STORAGE_KEY)

      if (stored) {
        try {
          const storedValues = JSON.parse(stored) as Record<string, unknown>
          const profile = getProfileFromStoredValues(storedValues)
          const hasValidEmail = Boolean(profile?.email)

          const cacheField = fields?.find((f) => f.adminLabel === 'sg_cache_id')
          const currentCacheId = cacheField?.defaultValue
          const storedCacheId = cacheField
            ? storedValues[`input_${cacheField.id}`]
            : undefined
          const cacheValid = !cacheField || storedCacheId === currentCacheId

          if (!cacheValid) {
            localStorage.removeItem(DOWNLOAD_FORM_STORAGE_KEY)
          } else if (hasValidEmail) {
            const overrideByInputId = buildOverrideByInputId(title, url)

            const submitData = { ...storedValues, ...overrideByInputId }

            setIsSilentSubmitting(true)
            if (profile) setUser(profile)
            axiosServer
              .post(
                `${API.GRAVITY_FORM}/${formId}/submissions`,
                convertJsontoFormData(submitData),
                { headers: { 'Content-Type': 'multipart/form-data' } }
              )
              .then((response) => {
                setStatusMessage(response.data.confirmation_message)
                setFeedbackStatus(response.data.is_valid ? 'success' : 'error')
                if (response.data.is_valid) {
                  localStorage.setItem(
                    DOWNLOAD_FORM_STORAGE_KEY,
                    JSON.stringify(submitData)
                  )
                }
                setFeedbackOpen(true)
              })
              .finally(() => {
                setIsSilentSubmitting(false)
              })
            return
          }
        } catch {
          // JSON malformato → fallback a modale
        }
      }

      openModalWithOverrides(title, url)
    },
    [
      fields,
      formId,
      getProfileFromStoredValues,
      buildOverrideByInputId,
      openModalWithOverrides
    ]
  )

  const handleFormSuccess = (values: any) => {
    localStorage.setItem(DOWNLOAD_FORM_STORAGE_KEY, JSON.stringify(values))
    setTimeout(() => {
      const profile = getProfileFromStoredValues(values)
      if (profile) setUser(profile)
    }, 100)
  }

  return (
    <Section data-layout="media-page" ref={rootRef}>
      {/* Hero title */}
      <Spacer variant="xxl" />
      <Container className="mb-spacing-xs lg:mb-spacing-2xl">
        <div className="grid grid-cols-2 gap-x-gutter md:grid-cols-6 lg:grid-cols-12">
          <div className="col-span-2 flex items-end justify-between md:col-span-6 lg:col-span-12">
            <Typography variant="h1" component="h1">
              {title}
            </Typography>
            <ScrollCta />
          </div>
          {user?.email ? (
            <div className="col-span-2 mt-spacing-sm flex items-center gap-spacing-sm md:col-span-6 lg:col-span-12">
              <Typography variant="small" component="p">
                {(
                  strings?.labels?.download_user_profiling ||
                  'Ti sei registrato come {user.name} {user.surname} e i download saranno inviati a {user.email}.'
                )
                  .replace('{user.name}', user.name || '')
                  .replace('{user.surname}', user.surname || '')
                  .replace('{user.email}', user.email || '')}
              </Typography>
              <CtaButton
                type="button"
                variant="secondary"
                typography="small"
                className="condensed cursor-pointer !space-x-spacing-2xs place-self-start whitespace-nowrap !p-spacing-2xs"
                endIcon={<SVGIcon symbol="close" aria-hidden="true" />}
                onClick={() => {
                  if (
                    confirm(
                      strings?.labels?.download_user_profiling_delete_confirm ||
                        'Sei sicuro di voler cancellare i dati salvati?'
                    )
                  ) {
                    localStorage.removeItem(DOWNLOAD_FORM_STORAGE_KEY)
                    setUser({})
                  }
                }}
              >
                {strings?.labels?.download_user_profiling_delete ||
                  'Rimuovi i dati salvati'}
              </CtaButton>
            </div>
          ) : null}
        </div>
        <Spacer variant="xl" />

        <Modal
          open={modalOpen}
          closeLabel={strings?.labels?.close || 'Chiudi'}
          onClose={() => setModalOpen(false)}
          data-lenis-prevent
          className="[&_[data-modal-close]]:hidden"
        >
          <ReactLenis
            className="max-h-[100vh] overflow-hidden py-spacing-lg"
            options={{ smoothWheel: true, syncTouch: true }}
            data-lenis-prevent
          >
            <div
              ref={modalRef}
              className="mx-auto max-w-[89%] bg-[#D9D9D9] p-spacing-lg lg:w-[100%] lg:max-w-[615px]"
            >
              <div className="flex gap-spacing-lg">
                <Typography
                  variant="large"
                  component="h2"
                  className="condensed mb-spacing-sm text-text-heading-light"
                >
                  {description}
                </Typography>
                <button
                  type="button"
                  aria-label={strings?.labels?.close || 'Chiudi'}
                  onClick={() => setModalOpen(false)}
                  className={cn(
                    'mt-[-6px] flex size-[60px] flex-[0_0_60px] items-center justify-center rounded-full border border-border-secondary-light/20 text-[white] backdrop-blur-[17px]',
                    'right-[50%] top-[10px] translate-x-1/2 scale-[0.8] md:right-[19px] md:top-[19px] md:translate-x-0 md:scale-100'
                  )}
                >
                  <SVGIcon
                    symbol="close"
                    className="size-[16px] text-text-button-secondary-light"
                  />
                </button>
              </div>
              <Spacer variant="md" />
              {formId ? (
                <Form
                  form_id={formId}
                  fields={enhancedFields}
                  submit={buttonText}
                  onSuccess={handleFormSuccess}
                  onCloseFeedback={() => setModalOpen(false)}
                  fieldOverrides={fieldOverrides}
                  selectDependencies={selectDependencies}
                  onFieldChange={handleFormFieldChange}
                  className={cn(
                    '!gap-spacing-lg',
                    '[&_[data-form-submit]_button]:bg-surface-primary-light [&_[data-form-submit]_button]:text-text-heading-dark',
                    '[&_[data-form-submit]_button_span]:!opacity-100'
                  )}
                  variant="light"
                />
              ) : null}
            </div>
          </ReactLenis>
        </Modal>

        {isSilentSubmitting && (
          <div className="fixed inset-0 z-[109] flex items-center justify-center bg-[black]/50">
            <Spinner />
          </div>
        )}

        {portalMounted
          ? createPortal(
              <div
                ref={feedbackModalRef}
                className="pointer-events-none fixed inset-0 z-[110] flex h-[calc(100*var(--lvh))] items-center justify-center bg-[black]/65 opacity-0"
                role="dialog"
                aria-modal="true"
                aria-hidden={!feedbackOpen}
                data-lenis-prevent={true}
              >
                <div className="mx-spacing-md grid w-full max-w-[50rem] bg-text-primary-light p-spacing-lg text-white-light">
                  <div className="text-center">
                    <Typography
                      variant="regular"
                      className={cn({
                        'text-white-light': feedbackStatus !== 'error',
                        'text-red-500': feedbackStatus === 'error'
                      })}
                    >
                      {parse(statusMessage)}
                    </Typography>
                  </div>
                  <Button
                    ref={okButtonRef}
                    type="button"
                    variant="primary"
                    onClick={() => {
                      setFeedbackOpen(false)
                      setStatusMessage('')
                    }}
                    className="mt-spacing-sm"
                  >
                    OK
                  </Button>
                </div>
              </div>,
              document.body
            )
          : null}

        {pageProps?.collection &&
          pageProps.collection.length > 0 &&
          pageProps.collection.map((item, index) => {
            return (
              <div
                key={index}
                className="w-full flex-wrap gap-spacing-sm border-b border-b-[rgba(0,0,0,.2)] pb-spacing-sm pt-spacing-md md:flex"
              >
                <div className="flex gap-spacing-xs md:gap-spacing-sm">
                  <div
                    className={cn(
                      'flex items-center justify-center rounded bg-surface-primary-light text-text-primary-invert-light dark:bg-surface-primary-dark dark:text-text-primary-invert-dark',
                      'size-[27px]',
                      'gap-spacing-md'
                    )}
                  >
                    <SVGIcon
                      symbol="pdf"
                      className={cn('h-[9px] w-[8px]')}
                      aria-hidden="true"
                    />
                  </div>
                  <Typography
                    variant="h5"
                    component="h2"
                    className="mt-[.3rem]"
                  >
                    {item?.title}
                  </Typography>
                </div>
                <div className="mb-spacing-xs mt-[-.3rem] pl-[4.4rem] md:mb-0 md:flex-1 md:pl-0 md:text-center"></div>
                <div className="mt-[0.4rem] flex flex-col gap-spacing-2xs pl-[4.4rem] md:flex-row md:items-center md:justify-start md:gap-spacing-sm md:pl-0">
                  {item.category?.length > 0 &&
                    item.category.map((cat: any, i: number) => (
                      <Typography
                        key={`cat-${i}`}
                        variant="regular"
                        className="condensed text-text-primary-light"
                      >
                        {cat.title}
                      </Typography>
                    ))}
                  <CtaButton
                    // href={item?.download?.url}
                    onClick={(e) => {
                      e.preventDefault()
                      updateDownloadFields(
                        item?.download?.title,
                        item?.download?.url
                      )
                    }}
                    target={'_blank'}
                    variant="secondary"
                    typography="small"
                    className="condensed cursor-pointer !space-x-spacing-2xs place-self-start whitespace-nowrap !p-spacing-2xs"
                  >
                    {strings?.download_pdf || 'Download PDF'}
                  </CtaButton>
                </div>
              </div>
            )
          })}
      </Container>

      {/* Post grid */}
      <Container></Container>
      <Spacer variant="xxl" />
    </Section>
  )
}

export default DownloadPage
